import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AddIcon from '@mui/icons-material/Add';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Feedback,
  Cycle,
  DashboardStats,
  PersonalDevelopmentGoal,
  ManagerFeedbackFillStatus,
  ReverseFeedback,
  TimeclockDashboardSummary,
  TimeClockEntryKind,
} from '../types';
import {
  formatDateTimeBr,
  formatCycleDateBr,
  isCycleClosedForSubmissions,
  shouldHighlightTeamFeedbackRow,
} from '../utils/cycleFeedbackUi';
import BehavioralChartsPreview from '../components/BehavioralChartsPreview';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import HolidayCalendar from '../components/HolidayCalendar';
import PageContainer from '../components/ui/PageContainer';
import StatusChip, { StatusTone } from '../components/ui/StatusChip';
import PunchStepCard, { PunchKind } from '../components/ui/PunchStepCard';
import { formatBonusUnjustifiedCounter } from '../utils/bonusEligibilityUi';
import { computeLiveDayBalanceMinutes } from '../utils/timeclockLiveBalance';
import { useStandupPendencias } from '../hooks/useStandupPendencias';

function formatWorkDateLocal(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

function formatTcMinutes(m: number): string {
  const sign = m < 0 ? '−' : '';
  const a = Math.abs(m);
  return `${sign}${Math.floor(a / 60)}h ${a % 60}min`;
}

function formatTcTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const toDateKey = (value: string | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

const formatDateBrShort = (value: string | undefined): string => {
  const k = toDateKey(value);
  if (k.length < 10) return '—';
  const [y, m, d] = k.split('-');
  return `${d}/${m}/${y}`;
};

/** Prazo previsto: vermelho se vencido; amarelo se falta até 7 dias (inclusive). */
function plannedEndHighlight(
  plannedEndDate: string | undefined,
  developed: boolean,
): 'is-ok' | 'is-warning' | 'is-danger' {
  if (developed || !plannedEndDate) return 'is-ok';
  const key = toDateKey(plannedEndDate);
  if (key.length < 10) return 'is-ok';
  const [y, m, d] = key.split('-').map(Number);
  const planned = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((planned.getTime() - today.getTime()) / (86400 * 1000));
  if (diffDays < 0) return 'is-danger';
  if (diffDays <= 7) return 'is-warning';
  return 'is-ok';
}

function isCompetencyDeadlineUrgent(
  plannedEndDate: string | undefined,
  developed: boolean,
): boolean {
  if (developed) return false;
  const h = plannedEndHighlight(plannedEndDate, developed);
  return h === 'is-warning' || h === 'is-danger';
}

/** Tom (StatusChip) do selo de prazo previsto. */
function plannedEndTone(highlight: 'is-ok' | 'is-warning' | 'is-danger'): StatusTone {
  if (highlight === 'is-warning') return 'warning';
  if (highlight === 'is-danger') return 'danger';
  return 'accent';
}

/**
 * Cor de fundo pastel da linha da tabela conforme proximidade do prazo
 * (aviso / vencido). Retorna um `sx` de fundo, ou undefined quando neutro.
 */
function pdDeadlineRowSx(
  plannedEndDate: string | undefined,
  developed: boolean,
): Record<string, unknown> | undefined {
  const h = plannedEndHighlight(plannedEndDate, developed);
  if (h === 'is-warning') {
    return { backgroundColor: 'rgba(237, 108, 2, 0.10)', '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.14)' } };
  }
  if (h === 'is-danger') {
    return { backgroundColor: 'rgba(211, 47, 47, 0.10)', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.14)' } };
  }
  return undefined;
}

function pdActivityDisplay(value: string | null | undefined): string {
  const t = value?.trim();
  return t && t.length > 0 ? t : '—';
}

function ActivityCellContent({ kind, value }: { kind: string; value: string | null | undefined }): React.ReactElement {
  return (
    <Box>
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: '0.68rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'text.secondary',
        }}
      >
        {kind}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: '0.85rem', whiteSpace: 'normal' }}>
        {pdActivityDisplay(value)}
      </Typography>
    </Box>
  );
}

function DashboardPdActivityCells({ goal }: { goal: PersonalDevelopmentGoal }): React.ReactElement {
  return (
    <>
      <TableCell>
        <ActivityCellContent kind="Livro" value={goal.activityBook} />
      </TableCell>
      <TableCell>
        <ActivityCellContent kind="Filme" value={goal.activityMovie} />
      </TableCell>
      <TableCell>
        <ActivityCellContent kind="Atividades" value={goal.activityTask} />
      </TableCell>
    </>
  );
}

/** Rótulo (kicker) padrão de secção. */
function SectionKicker({
  children,
  accent,
}: {
  children: React.ReactNode;
  accent?: boolean;
}): React.ReactElement {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: accent ? 'primary.main' : 'text.secondary',
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

const SURFACE_SX = { p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' } as const;

function dashBalanceColor(minutes: number): string {
  if (minutes < 0) return 'error.main';
  if (minutes > 0) return 'success.main';
  return 'text.primary';
}

/** Compact KPI tile: icon badge + label + value + optional chip/sub-line. */
function KpiTile({
  icon,
  label,
  value,
  valueColor,
  chip,
  sub,
  tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  chip?: { label: string; tone: StatusTone };
  sub?: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning';
}): React.ReactElement {
  const badgeBg =
    tone === 'success' ? 'success.main' : tone === 'warning' ? 'warning.main' : 'primary.main';
  return (
    <Paper sx={{ p: 2, backgroundColor: 'action.hover', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            bgcolor: badgeBg,
            color: '#fff',
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
        <Typography
          component="span"
          sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.1, color: valueColor }}>
          {value}
        </Typography>
        {chip ? <StatusChip label={chip.label} tone={chip.tone} /> : null}
      </Stack>
      {sub ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

const Dashboard: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const { data: pendencias } = useStandupPendencias();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [pdGoals, setPdGoals] = useState<PersonalDevelopmentGoal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cycleTeamStatus, setCycleTeamStatus] = useState<ManagerFeedbackFillStatus | null>(null);
  const [cycleTeamStatusError, setCycleTeamStatusError] = useState(false);
  const [pdCompleteLoadingId, setPdCompleteLoadingId] = useState<string | null>(null);
  const [pdFinishDates, setPdFinishDates] = useState<Record<string, string>>({});
  const [timeclockDash, setTimeclockDash] = useState<TimeclockDashboardSummary | null>(null);
  const [punchModalKind, setPunchModalKind] = useState<TimeClockEntryKind | null>(null);
  const [punchInProgress, setPunchInProgress] = useState(false);
  const [alert, setAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  }>({ isOpen: false, message: '', type: 'error' });
  const [devPdOngoingOpen, setDevPdOngoingOpen] = useState(true);
  const [devPdDoneOpen, setDevPdDoneOpen] = useState(true);
  const [teamPdGoalsOpen, setTeamPdGoalsOpen] = useState(true);

  const loadDashboardData = useCallback(async (): Promise<void> => {
    if (!user) return;

    setLoading(true);
    setTimeclockDash(null);
    setCycleTeamStatus(null);
    setCycleTeamStatusError(false);

    try {
      const [feedbacks, activeCycle] = await Promise.all([
        api.get<Feedback[]>('/feedbacks').then((response) => response.data),
        api
          .get<Cycle>('/cycles/active')
          .then((response) => response.data)
          .catch(() => undefined),
      ]);

      const canLoadTeamStatus =
        user.role === 'admin' || user.role === 'hr' || user.role === 'manager';
      if (canLoadTeamStatus && activeCycle?.id) {
        try {
          const teamRes = await api.get<ManagerFeedbackFillStatus>(
            `/cycles/${activeCycle.id}/manager-feedback-status`,
          );
          setCycleTeamStatus(teamRes.data);
        } catch {
          setCycleTeamStatus(null);
          setCycleTeamStatusError(true);
        }
      } else {
        setCycleTeamStatus(null);
      }

      if (user.role === 'developer') {
        let pdMine: PersonalDevelopmentGoal[] = [];
        let reverseList: ReverseFeedback[] = [];
        try {
          pdMine = (await api.get<PersonalDevelopmentGoal[]>('/personal-development/my-goals')).data;
        } catch (e) {
          console.error('Erro ao carregar minhas metas:', e);
          pdMine = [];
        }
        try {
          reverseList = (await api.get<ReverseFeedback[]>('/feedbacks/reverse')).data;
        } catch (e) {
          console.error('Erro ao carregar feedbacks reversos:', e);
          reverseList = [];
        }

        const cycleId = activeCycle?.id;
        const managerFbThisCycle =
          cycleId != null ? feedbacks.find((f) => f.cycleId === cycleId) ?? null : null;
        const reverseThisCycle =
          cycleId != null ? reverseList.find((r) => r.cycleId === cycleId) ?? null : null;

        setStats({
          type: 'developer',
          activeCycle,
          pdGoalsMine: pdMine,
          activeCycleManagerFeedback: managerFbThisCycle,
          activeCycleReverseFeedback: reverseThisCycle,
        });
        setPdGoals([]);
      } else if (user.role === 'manager') {
        const myFeedbacks = feedbacks.filter((feedback) => feedback.managerId === user.id);
        const teamStats: Record<string, { name: string; scores: number[]; count: number }> = {};

        myFeedbacks.forEach((feedback) => {
          if (feedback.developer && !teamStats[feedback.developer.id]) {
            teamStats[feedback.developer.id] = {
              name: feedback.developer.name,
              scores: [],
              count: 0,
            };
          }

          if (feedback.developer) {
            teamStats[feedback.developer.id].scores.push(parseFloat(feedback.finalScore));
            teamStats[feedback.developer.id].count++;
          }
        });

        const teamData = Object.values(teamStats).map((member) => ({
          name: member.name,
          average: (member.scores.reduce((sum, score) => sum + score, 0) / member.scores.length).toFixed(2),
          count: member.count,
        }));

        setStats({
          type: 'manager',
          totalFeedbacks: myFeedbacks.length,
          teamAverage:
            myFeedbacks.length > 0
              ? (
                  myFeedbacks.reduce((sum, feedback) => sum + parseFloat(feedback.finalScore), 0) /
                  myFeedbacks.length
                ).toFixed(2)
              : '0',
          teamData,
          activeCycle,
        });
      } else {
        setStats({
          type: 'admin',
          totalFeedbacks: feedbacks.length,
          activeCycle,
        });
      }

      if (user.role === 'admin' || user.role === 'hr' || user.role === 'manager') {
        try {
          const pdRes = await api.get<PersonalDevelopmentGoal[]>(
            '/personal-development/dashboard-goals',
          );
          setPdGoals(pdRes.data);
        } catch (e) {
          console.error('Erro ao carregar metas de desenvolvimento:', e);
          setPdGoals([]);
        }
      }

      const canLoadTimeclock =
        user.role === 'admin' ||
        user.role === 'hr' ||
        user.role === 'manager' ||
        user.role === 'developer';
      if (canLoadTimeclock) {
        try {
          const tcSummary = (
            await api.get<TimeclockDashboardSummary>('/timeclock/my/dashboard-summary')
          ).data;
          setTimeclockDash(tcSummary);
        } catch (e) {
          console.error('Erro ao carregar resumo de ponto:', e);
          setTimeclockDash(null);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setPdGoals([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [loadDashboardData, user]);

  useEffect(() => {
    if (
      !user ||
      (user.role !== 'developer' &&
        user.role !== 'manager' &&
        user.role !== 'admin' &&
        user.role !== 'hr')
    ) {
      return;
    }
    const refreshTimeclock = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      try {
        const tc = (await api.get<TimeclockDashboardSummary>('/timeclock/my/dashboard-summary')).data;
        setTimeclockDash(tc);
      } catch {
        /* mantém último resumo */
      }
    };
    const intervalId = window.setInterval(() => void refreshTimeclock(), 5000);
    return () => window.clearInterval(intervalId);
  }, [user]);

  const todayPunchState = useMemo(() => {
    const todayWorkDate = formatWorkDateLocal();
    const todayDay = timeclockDash?.recentDays.find((d) => d.workDate === todayWorkDate);
    const tp = timeclockDash?.todayPunches;
    const hasCheckIn = !!(tp?.checkInAt ?? todayDay?.firstCheckInAt);
    const hasPrimaryCheckOut = !!tp?.checkOutAt;
    const hasLunchOut = !!tp?.lunchOutAt;
    const hasLunchIn = !!tp?.lunchInAt;
    const hasCompletedPrimary =
      tp?.hasCompletedPrimary ?? !!(hasCheckIn && hasPrimaryCheckOut);
    const sessionOpen =
      tp?.sessionOpen ?? (hasCheckIn && !hasCompletedPrimary);
    const canReopen = tp?.canReopen ?? (hasCompletedPrimary && !sessionOpen);
    return {
      hasCheckIn,
      hasCheckOut: hasPrimaryCheckOut,
      checkInAt: tp?.checkInAt ?? todayDay?.firstCheckInAt ?? null,
      checkOutAt: tp?.checkOutAt ?? null,
      hasLunchOut,
      hasLunchIn,
      lunchOutAt: tp?.lunchOutAt ?? null,
      lunchInAt: tp?.lunchInAt ?? null,
      sessionOpen,
      hasCompletedPrimary,
      canReopen,
    };
  }, [timeclockDash]);

  /** Contador regressivo só na UI (não altera saldo/banco persistido). */
  const [liveNow, setLiveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!todayPunchState.sessionOpen || !todayPunchState.checkInAt || todayPunchState.hasCheckOut) {
      return;
    }
    const id = window.setInterval(() => setLiveNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [todayPunchState.sessionOpen, todayPunchState.checkInAt, todayPunchState.hasCheckOut]);

  const todayLiveBalanceMinutes = useMemo(() => {
    if (!todayPunchState.sessionOpen || !todayPunchState.checkInAt || todayPunchState.hasCheckOut) {
      return null;
    }
    return computeLiveDayBalanceMinutes({
      checkInAt: todayPunchState.checkInAt,
      checkOutAt: todayPunchState.checkOutAt,
      lunchOutAt: todayPunchState.lunchOutAt,
      lunchInAt: todayPunchState.lunchInAt,
      now: new Date(liveNow),
    });
  }, [todayPunchState, liveNow]);

  const punchSteps = useMemo(() => {
    const s = todayPunchState;
    const lunchLocked = s.hasCompletedPrimary;
    const steps: Array<{
      kind: TimeClockEntryKind;
      label: string;
      doneLabel: string;
      enabled: boolean;
      done: boolean;
      title?: string;
      variant: 'work' | 'lunch';
    }> = [
      {
        kind: 'check_in',
        label: s.canReopen || (s.hasCompletedPrimary && !s.sessionOpen) ? 'Reabrir expediente' : '1 · Entrada',
        doneLabel: s.sessionOpen
          ? s.hasCompletedPrimary
            ? 'Expediente reaberto'
            : s.checkInAt
              ? `✓ Entrada ${formatTcTime(s.checkInAt)}`
              : '✓ Entrada'
          : s.checkInAt
            ? `✓ Entrada ${formatTcTime(s.checkInAt)}`
            : '✓ Entrada',
        enabled: !s.sessionOpen,
        done: s.hasCheckIn && s.sessionOpen,
        title: s.sessionOpen
          ? 'Já há um expediente aberto'
          : s.canReopen
            ? 'Registrar nova entrada (hora extra)'
            : 'Registrar entrada do expediente',
        variant: 'work',
      },
      {
        kind: 'lunch_out',
        label: '2 · Ir ao almoço',
        doneLabel: s.lunchOutAt ? `✓ Almoço ${formatTcTime(s.lunchOutAt)}` : '✓ Ir ao almoço',
        enabled: s.sessionOpen && !lunchLocked && !s.hasLunchOut,
        done: s.hasLunchOut,
        title: lunchLocked
          ? 'Almoço bloqueado após a primeira saída do expediente'
          : s.hasLunchOut
            ? `Saída para almoço${s.lunchOutAt ? ` às ${formatTcTime(s.lunchOutAt)}` : ''}`
            : !s.sessionOpen
              ? 'Abra o expediente antes do almoço'
              : 'Registrar saída para o almoço',
        variant: 'lunch',
      },
      {
        kind: 'lunch_in',
        label: '3 · Volta do almoço',
        doneLabel: s.lunchInAt ? `✓ Volta ${formatTcTime(s.lunchInAt)}` : '✓ Volta do almoço',
        enabled: s.sessionOpen && !lunchLocked && s.hasLunchOut && !s.hasLunchIn,
        done: s.hasLunchIn,
        title: lunchLocked
          ? 'Almoço bloqueado após a primeira saída do expediente'
          : s.hasLunchIn
            ? `Volta do almoço${s.lunchInAt ? ` às ${formatTcTime(s.lunchInAt)}` : ''}`
            : !s.hasLunchOut
              ? 'Registre a saída para o almoço antes'
              : 'Registrar volta do almoço',
        variant: 'lunch',
      },
      {
        kind: 'check_out',
        label: s.hasCompletedPrimary ? 'Encerrar expediente' : '4 · Saída',
        doneLabel: s.checkOutAt ? `✓ Saída ${formatTcTime(s.checkOutAt)}` : '✓ Saída',
        enabled: s.sessionOpen && !(s.hasLunchOut && !s.hasLunchIn && !lunchLocked),
        done: s.hasCompletedPrimary && !s.sessionOpen,
        title: !s.sessionOpen
          ? s.canReopen
            ? 'Expediente encerrado — você pode reabrir para hora extra'
            : 'Registre a entrada antes'
          : s.hasLunchOut && !s.hasLunchIn && !lunchLocked
            ? 'Registre a volta do almoço antes da saída'
            : 'Registrar saída do expediente',
        variant: 'work',
      },
    ];
    return steps;
  }, [todayPunchState]);

  const nextPunchKind = useMemo(
    () => punchSteps.find((step) => step.enabled)?.kind ?? null,
    [punchSteps],
  );

  const refreshTimeclockSummary = useCallback(async (): Promise<void> => {
    const tc = (await api.get<TimeclockDashboardSummary>('/timeclock/my/dashboard-summary')).data;
    setTimeclockDash(tc);
  }, []);

  const executeDashboardPunch = useCallback(async (): Promise<void> => {
    if (!punchModalKind || punchInProgress) return;
    setPunchInProgress(true);
    try {
      await api.post('/timeclock/punch', { kind: punchModalKind });
      await refreshTimeclockSummary();
      setPunchModalKind(null);
    } catch {
      // keep modal open so user can retry / cancel
    } finally {
      setPunchInProgress(false);
    }
  }, [punchModalKind, punchInProgress, refreshTimeclockSummary]);

  const punchConfirmMessage = useMemo(() => {
    const nowBr = new Date().toLocaleTimeString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
    });
    if (!punchModalKind) return '';
    const labels: Record<TimeClockEntryKind, string> = {
      check_in: 'entrada',
      check_out: 'saída do expediente',
      lunch_out: 'ida ao almoço',
      lunch_in: 'volta do almoço',
    };
    return `Registrar sua ${labels[punchModalKind]} agora (${nowBr}, horário de Brasília)?`;
  }, [punchModalKind]);

  const punchModalTitle = useMemo(() => {
    if (!punchModalKind) return '';
    const titles: Record<TimeClockEntryKind, string> = {
      check_in: 'Confirmar entrada',
      check_out: 'Confirmar saída do expediente',
      lunch_out: 'Confirmar ida ao almoço',
      lunch_in: 'Confirmar volta do almoço',
    };
    return titles[punchModalKind];
  }, [punchModalKind]);

  const firstName = useMemo(() => user?.name?.split(' ')[0] || 'Equipe', [user?.name]);
  const teamData = stats?.teamData || [];
  const developersTracked = teamData.length;
  const hasActiveCycle = Boolean(stats?.activeCycle);
  const cycleSubmissionsOpen = Boolean(
    stats?.activeCycle && !isCycleClosedForSubmissions(stats.activeCycle.deadlineDate),
  );

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const markPdGoalDeveloped = useCallback(
    async (goalId: string): Promise<void> => {
      const date = pdFinishDates[goalId] ?? todayStr;
      setPdCompleteLoadingId(goalId);
      try {
        await api.patch(`/personal-development/${goalId}`, {
          developed: true,
          actualEndDate: date,
        });
        await loadDashboardData();
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Não foi possível concluir a competência.';
        setAlert({ isOpen: true, message: msg, type: 'error' });
      } finally {
        setPdCompleteLoadingId(null);
      }
    },
    [pdFinishDates, todayStr, loadDashboardData],
  );

  const showDevelopmentGoalsBlock = useMemo(
    () => user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager',
    [user?.role],
  );

  const roleDescription = useMemo(() => {
    switch (user?.role) {
      case 'developer':
        return 'Acompanhe sua evolução, histórico de feedbacks e desempenho por ciclo.';
      case 'manager':
        return 'Acompanhe o time, prazos de desenvolvimento individual e devolutivas de feedback.';
      case 'admin':
      case 'hr':
        return 'Tenha uma visão ampla da operação, dos ciclos ativos e do volume de feedbacks.';
      default:
        return 'Acompanhe indicadores e resultados da plataforma.';
    }
  }, [user?.role]);

  const canUseTimeclock = useMemo(
    () =>
      user?.role === 'admin' ||
      user?.role === 'hr' ||
      user?.role === 'manager' ||
      user?.role === 'developer',
    [user?.role],
  );

  const canAccessTimeclockReports = useMemo(
    () => user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager',
    [user?.role],
  );

  const formatDate = (value?: string): string => {
    if (!value) return 'Não definido';
    return new Date(value).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <PageContainer>
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 8 }} spacing={2}>
          <CircularProgress />
          <Box sx={{ textAlign: 'center' }}>
            <StatusChip label="Carregando" tone="accent" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, mt: 1.5 }}>
              Preparando seu painel
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Organizando indicadores, gráficos e atalhos da sua área.
            </Typography>
          </Box>
        </Stack>
      </PageContainer>
    );
  }

  if (!stats || !user) {
    return (
      <PageContainer>
        <Paper sx={{ ...SURFACE_SX, textAlign: 'center', py: 6 }}>
          <StatusChip label="Sem dados" tone="neutral" />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, mt: 1.5 }}>
            Nenhum dado disponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Não encontramos informações para montar o dashboard neste momento.
          </Typography>
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={`Olá, ${firstName}`} subtitle={roleDescription} maxWidth="xl" icon={<SpaceDashboardOutlinedIcon />}>
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* Pendências: primeiro item do dashboard, layout compacto */}
        {user.role === 'developer' && pendencias && (pendencias.missingStandups > 0 || pendencias.incompleteWorkDays > 0 || pendencias.missingToday) && (
          <Paper sx={{ p: { xs: 1.75, md: 2 }, border: '1px solid', borderColor: 'warning.main' }} aria-label="Pendências">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1.5, md: 2 }}
              sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                <AssignmentLateOutlinedIcon color="warning" />
                <Box sx={{ minWidth: 0 }}>
                  <SectionKicker>Pendências</SectionKicker>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                    {pendencias.missingToday && <StatusChip label="Stand-up de hoje" tone="warning" />}
                    {pendencias.missingStandups > 0 && (
                      <StatusChip
                        label={`${pendencias.missingStandups} stand-up${pendencias.missingStandups > 1 ? 's' : ''} em aberto`}
                        tone="warning"
                      />
                    )}
                    {pendencias.incompleteWorkDays > 0 && (
                      <StatusChip
                        label={`${pendencias.incompleteWorkDays} dia${pendencias.incompleteWorkDays > 1 ? 's' : ''} sem jornada completa`}
                        tone="warning"
                      />
                    )}
                    {pendencias.missingStandups === 0 && pendencias.incompleteWorkDays === 0 && pendencias.missingToday && (
                      <Typography variant="body2" color="text.secondary">
                        Registre o stand-up de hoje — leva menos de um minuto.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1 }}>
                <Button component={Link} to="/standups" size="small" variant="contained" color="primary" startIcon={<ViewDayOutlinedIcon />}>
                  {pendencias.missingToday ? 'Registrar hoje' : 'Abrir stand-up'}
                </Button>
                {pendencias.incompleteWorkDays > 0 && canUseTimeclock && (
                  <Button component={Link} to="/ponto-eletronico" size="small" variant="outlined">
                    Abrir ponto
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Hero: ciclo ativo + registro de ponto + ações rápidas */}
        <Paper sx={SURFACE_SX}>
          {/* Ciclo ativo — status strip */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stats.activeCycle ? 'primary.contrastText' : 'text.secondary',
                bgcolor: stats.activeCycle ? 'primary.main' : 'action.selected',
                borderRadius: 1,
                '& svg': { fontSize: 24 },
              }}
            >
              <EventAvailableOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Ciclo ativo
                </Typography>
                <StatusChip
                  label={stats.activeCycle ? (cycleSubmissionsOpen ? 'Aberto' : 'Encerrado') : 'Inativo'}
                  tone={stats.activeCycle ? (cycleSubmissionsOpen ? 'success' : 'neutral') : 'neutral'}
                />
              </Stack>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.3 }}>
                {stats.activeCycle?.name || 'Nenhum ciclo ativo'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.activeCycle
                  ? cycleSubmissionsOpen
                    ? `Período até ${formatCycleDateBr(stats.activeCycle.endDate)} · envios até ${formatCycleDateBr(stats.activeCycle.deadlineDate)}`
                    : `Período até ${formatCycleDateBr(stats.activeCycle.endDate)} · envios encerrados em ${formatCycleDateBr(stats.activeCycle.deadlineDate)}`
                  : 'Ative um ciclo para liberar o acompanhamento do período.'}
              </Typography>
            </Box>
          </Box>

          {canUseTimeclock ? (
            <Box sx={{ mt: 3 }} aria-label="Registro de ponto do dia">
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Registro de ponto · hoje
                </Typography>
                {todayLiveBalanceMinutes != null ? (
                  <StatusChip label={`Saldo em curso ${formatTcMinutes(todayLiveBalanceMinutes)}`} tone="info" />
                ) : null}
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                {punchSteps.map((step, index) => {
                  const isNext = nextPunchKind === step.kind;
                  const disabled = punchInProgress || loading || !step.enabled;
                  const title =
                    step.kind === 'check_in'
                      ? 'Entrada'
                      : step.kind === 'lunch_out'
                        ? 'Ir ao almoço'
                        : step.kind === 'lunch_in'
                          ? 'Volta do almoço'
                          : 'Saída';
                  const time =
                    step.kind === 'check_in'
                      ? todayPunchState.checkInAt
                      : step.kind === 'lunch_out'
                        ? todayPunchState.lunchOutAt
                        : step.kind === 'lunch_in'
                          ? todayPunchState.lunchInAt
                          : todayPunchState.checkOutAt;
                  return (
                    <PunchStepCard
                      key={step.kind}
                      kind={step.kind as PunchKind}
                      title={title}
                      step={index + 1}
                      time={time ? formatTcTime(time) : null}
                      done={step.done}
                      next={isNext}
                      disabled={disabled}
                      loading={punchInProgress && isNext}
                      tooltip={step.title}
                      onClick={() => setPunchModalKind(step.kind)}
                    />
                  );
                })}
              </Box>
            </Box>
          ) : null}

          <Stack direction="row" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
            <Button component={Link} to="/feedbacks" variant="contained" color="primary" startIcon={<RateReviewOutlinedIcon />}>
              Ver feedbacks
            </Button>
            {canAccessTimeclockReports ? (
              <Button component={Link} to="/reports" variant="outlined">
                Abrir relatórios
              </Button>
            ) : null}
            {user.role === 'developer' && hasActiveCycle && cycleSubmissionsOpen && (
              <Button component={Link} to="/feedbacks/reverse/create" variant="outlined">
                Avaliar gestor
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Calendário de feriados (componente já migrado) */}
        <Paper sx={SURFACE_SX} aria-label="Calendário de feriados">
          <HolidayCalendar variant="surface" compact showTimeclockRequests={canUseTimeclock} />
        </Paper>

        {stats.type === 'developer' && (
          <>
            <Paper sx={SURFACE_SX}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, mb: 2 }}
              >
                <Box>
                  <SectionKicker>Este ciclo</SectionKicker>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                    Feedback do ciclo ativo
                  </Typography>
                  {!stats.activeCycle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                      Não há ciclo de feedback ativo. Quando existir, o estado do feedback do gestor e da sua
                      avaliação reversa aparecerão aqui.
                    </Typography>
                  ) : null}
                </Box>
                {stats.activeCycle ? (
                  <Button component={Link} to="/feedbacks" variant="outlined" size="small">
                    Ver todos os feedbacks
                  </Button>
                ) : null}
              </Stack>

              {stats.activeCycle ? (
                <>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography component="strong" sx={{ fontWeight: 800 }}>
                      {stats.activeCycle.name}
                    </Typography>
                    {cycleSubmissionsOpen ? (
                      <StatusChip label="Envios abertos" tone="success" />
                    ) : (
                      <StatusChip label="Envios encerrados" tone="neutral" />
                    )}
                  </Stack>

                  {(() => {
                    // ---- Step 1: feedback received from manager ----
                    const managerDone = Boolean(stats.activeCycleManagerFeedback);
                    // ---- Step 2: reverse feedback to the manager ----
                    const noManager = !user.manager?.id;
                    const reverseDone = Boolean(stats.activeCycleReverseFeedback);

                    const step1 = {
                      status: managerDone
                        ? { label: 'Recebido', tone: 'success' as StatusTone }
                        : { label: 'Aguardando', tone: 'warning' as StatusTone },
                      line: managerDone
                        ? `Registrado em ${formatDate(stats.activeCycleManagerFeedback?.createdAt)} · tratado diretamente com o gestor.`
                        : user.manager?.name
                          ? `${user.manager.name} ainda não registrou o feedback deste ciclo.`
                          : 'Seu gestor ainda não registrou o feedback deste ciclo.',
                    };

                    let step2: {
                      status: { label: string; tone: StatusTone };
                      line: string;
                      cta?: React.ReactNode;
                    };
                    if (noManager) {
                      step2 = {
                        status: { label: 'Indisponível', tone: 'neutral' },
                        line: 'Sem gestor associado ao seu perfil. Contate o RH para associar um gestor.',
                      };
                    } else if (reverseDone) {
                      step2 = {
                        status: { label: 'Enviada', tone: 'success' },
                        line: `Você enviou sua avaliação em ${formatDate(stats.activeCycleReverseFeedback?.createdAt)}.`,
                        cta: (
                          <Button
                            component={Link}
                            to={`/feedbacks/reverse/${stats.activeCycleReverseFeedback?.id}`}
                            variant="outlined"
                            size="small"
                          >
                            Ver minha avaliação
                          </Button>
                        ),
                      };
                    } else if (!cycleSubmissionsOpen) {
                      step2 = {
                        status: { label: 'Prazo encerrado', tone: 'neutral' },
                        line: 'O prazo de envios deste ciclo já encerrou — não é possível avaliar agora.',
                      };
                    } else if (!managerDone) {
                      step2 = {
                        status: { label: 'Em breve', tone: 'info' },
                        line: 'Libera assim que o gestor registrar o feedback deste ciclo.',
                      };
                    } else {
                      step2 = {
                        status: { label: 'Pendente', tone: 'warning' },
                        line: 'Sua vez: envie a avaliação do seu gestor neste ciclo.',
                        cta: (
                          <Button
                            component={Link}
                            to={`/feedbacks/reverse/create?cycleId=${encodeURIComponent(stats.activeCycle.id)}&managerId=${encodeURIComponent(user.manager!.id)}`}
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Avaliar gestor
                          </Button>
                        ),
                      };
                    }

                    const StepCard: React.FC<{
                      icon: React.ReactNode;
                      title: string;
                      status: { label: string; tone: StatusTone };
                      line: string;
                      cta?: React.ReactNode;
                    }> = ({ icon, title, status, line, cta }) => (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: 'action.hover',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                        >
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                            <Box sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: 20 } }}>
                              {icon}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {title}
                            </Typography>
                          </Stack>
                          <StatusChip label={status.label} tone={status.tone} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                          {line}
                        </Typography>
                        {cta ? <Box sx={{ mt: 1.5 }}>{cta}</Box> : null}
                      </Paper>
                    );

                    return (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <StepCard
                            icon={<MoveToInboxOutlinedIcon />}
                            title="Feedback do gestor"
                            status={step1.status}
                            line={step1.line}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <StepCard
                            icon={<ReplyOutlinedIcon />}
                            title="Sua avaliação ao gestor"
                            status={step2.status}
                            line={step2.line}
                            cta={step2.cta}
                          />
                        </Grid>
                      </Grid>
                    );
                  })()}
                </>
              ) : null}
            </Paper>

            <Paper sx={SURFACE_SX}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, mb: 2 }}
              >
                <Box>
                  <SectionKicker accent>Ponto</SectionKicker>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                    Ponto eletrônico e banco de horas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                    Saldo do banco de horas, elegibilidade ao bônus do mês e últimos dias registados.
                  </Typography>
                </Box>
                <Button component={Link} to="/ponto-eletronico" variant="outlined" size="small" startIcon={<OpenInNewIcon />}>
                  Abrir módulo
                </Button>
              </Stack>
              {timeclockDash ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <KpiTile
                        icon={<AccountBalanceWalletOutlinedIcon />}
                        label="Saldo banco de horas"
                        value={formatTcMinutes(timeclockDash.balanceMinutes)}
                        valueColor={dashBalanceColor(timeclockDash.balanceMinutes)}
                        tone={timeclockDash.balanceMinutes < 0 ? 'warning' : 'success'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <KpiTile
                        icon={<EmojiEventsOutlinedIcon />}
                        label="Bônus no mês"
                        tone={timeclockDash.bonus.eligible ? 'success' : 'warning'}
                        value={
                          <Box component="span" sx={{ fontSize: '1.05rem' }}>
                            {timeclockDash.bonus.eligible ? 'Elegível' : 'Não elegível'}
                          </Box>
                        }
                        chip={{
                          label: timeclockDash.bonus.eligible ? 'OK' : 'Atenção',
                          tone: timeclockDash.bonus.eligible ? 'success' : 'warning',
                        }}
                        sub={
                          <>
                            {formatBonusUnjustifiedCounter(timeclockDash.bonus)}
                            {!timeclockDash.bonus.eligible && timeclockDash.bonus.reason
                              ? ` · ${timeclockDash.bonus.reason}`
                              : ''}
                          </>
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <KpiTile
                        icon={<AssignmentLateOutlinedIcon />}
                        label="Justificativas pendentes"
                        value={timeclockDash.justificationsPending}
                        tone={timeclockDash.justificationsPending > 0 ? 'warning' : 'neutral'}
                        sub={timeclockDash.justificationsPending > 0 ? 'Aguardando análise do RH' : 'Nada pendente'}
                      />
                    </Grid>
                  </Grid>
                  <TableContainer component={Paper} sx={{ overflowX: 'auto', border: 1, borderColor: 'divider' }}>
                    <Table
                      size="small"
                      sx={{
                        minWidth: 640,
                        '& thead th': {
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'text.secondary',
                          bgcolor: 'action.hover',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          whiteSpace: 'nowrap',
                        },
                        '& tbody td': { borderColor: 'divider' },
                        '& .tc-time': { fontVariantNumeric: 'tabular-nums', textAlign: 'center', whiteSpace: 'nowrap' },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell align="center">Entrada</TableCell>
                          <TableCell align="center">Saída almoço</TableCell>
                          <TableCell align="center">Volta almoço</TableCell>
                          <TableCell align="center">Saída</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Saldo dia</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const rows = timeclockDash.recentDays
                            .filter((d) => d.firstCheckInAt || d.lastCheckOutAt || d.isAbsence || d.statusLabel === 'Zerado')
                            .slice(0, 7);
                          if (rows.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                  Nenhum dia registrado recentemente.
                                </TableCell>
                              </TableRow>
                            );
                          }
                          const muted = (v: string) =>
                            v === '—' ? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box> : v;
                          return rows.map((d) => {
                            const isToday = d.workDate === formatWorkDateLocal();
                            const saldo =
                              isToday && todayLiveBalanceMinutes != null
                                ? todayLiveBalanceMinutes
                                : d.dailyBalanceMinutes;
                            return (
                              <TableRow
                                key={d.id}
                                hover
                                sx={{
                                  '&:last-of-type td': { borderBottom: 0 },
                                  ...(isToday
                                    ? {
                                        bgcolor: 'rgba(43,108,176,0.06)',
                                        '& td:first-of-type': { borderLeft: '3px solid', borderColor: 'primary.main' },
                                      }
                                    : {}),
                                }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: isToday ? 800 : 600 }}>
                                  {formatDateBrShort(d.workDate)}
                                  {isToday ? (
                                    <Box component="span" sx={{ ml: 0.75, fontSize: '0.68rem', fontWeight: 800, color: 'primary.main' }}>
                                      HOJE
                                    </Box>
                                  ) : null}
                                </TableCell>
                                <TableCell className="tc-time">{muted(formatTcTime(d.firstCheckInAt))}</TableCell>
                                <TableCell className="tc-time">{muted(formatTcTime(d.lunchOutAt))}</TableCell>
                                <TableCell className="tc-time">{muted(formatTcTime(d.lunchInAt))}</TableCell>
                                <TableCell className="tc-time">{muted(formatTcTime(d.lastCheckOutAt))}</TableCell>
                                <TableCell>
                                  <StatusChip
                                    label={d.statusLabel || '—'}
                                    tone={saldo < 0 ? 'danger' : saldo > 0 ? 'success' : 'neutral'}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  <Box component="span" sx={{ color: dashBalanceColor(saldo), fontWeight: 700 }}>
                                    {formatTcMinutes(saldo)}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Reaberturas / hora extra</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Após encerrar o expediente, novas entradas e saídas entram como hora extra. O almoço
                      permanece bloqueado nessas reaberturas.
                    </Typography>
                    {(timeclockDash.extraSessions?.length ?? 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Nenhuma reabertura registrada nos últimos dias.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} sx={{ overflowX: 'auto', mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Entrada (extra)</TableCell>
                              <TableCell>Saída (extra)</TableCell>
                              <TableCell>Duração</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {timeclockDash.extraSessions!.slice(0, 10).map((s, idx) => (
                              <TableRow key={`${s.workDate}-${s.checkInAt}-${idx}`}>
                                <TableCell>{formatDateBrShort(s.workDate)}</TableCell>
                                <TableCell>{formatTcTime(s.checkInAt)}</TableCell>
                                <TableCell>
                                  {s.checkOutAt ? formatTcTime(s.checkOutAt) : 'Em andamento'}
                                </TableCell>
                                <TableCell>
                                  {s.minutes != null ? formatTcMinutes(s.minutes) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Não foi possível carregar o resumo de ponto. Utilize o módulo em RH → Ponto eletrônico.
                </Typography>
              )}
            </Paper>

            <Paper sx={SURFACE_SX}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, mb: 2 }}
              >
                <Box>
                  <SectionKicker>Desenvolvimento</SectionKicker>
                  <Typography variant="h6" component="h2" id="dash-dev-pd-ongoing-heading" sx={{ fontWeight: 800 }}>
                    Competências em andamento
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                    Atividades definidas pelo RH/admin. A linha fica em vermelho na última semana antes da
                    previsão ou se a data estiver vencida. Ao concluir, informe a data de finalização — o status
                    passa a concluído e fica visível no cadastro administrativo.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setDevPdOngoingOpen((v) => !v)}
                    aria-expanded={devPdOngoingOpen}
                    aria-controls="dash-dev-pd-ongoing-panel"
                  >
                    {devPdOngoingOpen ? 'Recolher' : 'Expandir'}
                  </Button>
                  <Button component={Link} to="/feedbacks" variant="outlined" size="small">
                    Ver feedbacks
                  </Button>
                </Stack>
              </Stack>

              {devPdOngoingOpen ? (
                <Box id="dash-dev-pd-ongoing-panel" role="region" aria-labelledby="dash-dev-pd-ongoing-heading">
                  {(stats.pdGoalsMine ?? []).filter((g) => !g.developed).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma competência em desenvolvimento no momento.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Competência</TableCell>
                            <TableCell>Livro</TableCell>
                            <TableCell>Filme</TableCell>
                            <TableCell>Atividades</TableCell>
                            <TableCell>Início</TableCell>
                            <TableCell>Previsão</TableCell>
                            <TableCell>Concluir</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(stats.pdGoalsMine ?? [])
                            .filter((g) => !g.developed)
                            .map((g) => {
                              const urgent = isCompetencyDeadlineUrgent(g.plannedEndDate, g.developed);
                              const rowSx = pdDeadlineRowSx(g.plannedEndDate, g.developed);
                              const highlight = plannedEndHighlight(g.plannedEndDate, g.developed);
                              return (
                                <TableRow
                                  key={g.id}
                                  sx={{
                                    ...(rowSx ?? {}),
                                    ...(urgent ? { '& td': { fontWeight: 600 } } : {}),
                                  }}
                                >
                                  <TableCell>
                                    <strong>{g.competencyTitle}</strong>
                                  </TableCell>
                                  <DashboardPdActivityCells goal={g} />
                                  <TableCell>{formatDateBrShort(g.startDate)}</TableCell>
                                  <TableCell>
                                    <StatusChip
                                      label={formatDateBrShort(g.plannedEndDate)}
                                      tone={plannedEndTone(highlight)}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                      <TextField
                                        type="date"
                                        size="small"
                                        value={pdFinishDates[g.id] ?? todayStr}
                                        onChange={(e) =>
                                          setPdFinishDates((prev) => ({ ...prev, [g.id]: e.target.value }))
                                        }
                                        sx={{ minWidth: 150 }}
                                      />
                                      <Button
                                        type="button"
                                        variant="outlined"
                                        size="small"
                                        disabled={pdCompleteLoadingId === g.id}
                                        onClick={() => void markPdGoalDeveloped(g.id)}
                                      >
                                        {pdCompleteLoadingId === g.id ? 'Salvando…' : 'Marcar concluída'}
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : null}
            </Paper>

            <Paper sx={SURFACE_SX}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}
              >
                <Box>
                  <SectionKicker>Histórico</SectionKicker>
                  <Typography variant="h6" component="h2" id="dash-dev-pd-done-heading" sx={{ fontWeight: 800 }}>
                    Competências já desenvolvidas
                  </Typography>
                </Box>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setDevPdDoneOpen((v) => !v)}
                  aria-expanded={devPdDoneOpen}
                  aria-controls="dash-dev-pd-done-panel"
                >
                  {devPdDoneOpen ? 'Recolher' : 'Expandir'}
                </Button>
              </Stack>
              {devPdDoneOpen ? (
                <Box id="dash-dev-pd-done-panel" role="region" aria-labelledby="dash-dev-pd-done-heading">
                  {(stats.pdGoalsMine ?? []).filter((g) => g.developed).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma competência concluída registrada ainda.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Competência</TableCell>
                            <TableCell>Livro</TableCell>
                            <TableCell>Filme</TableCell>
                            <TableCell>Atividades</TableCell>
                            <TableCell>Início</TableCell>
                            <TableCell>Previsão</TableCell>
                            <TableCell>Concluído em</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(stats.pdGoalsMine ?? [])
                            .filter((g) => g.developed)
                            .map((g) => (
                              <TableRow key={g.id}>
                                <TableCell>{g.competencyTitle}</TableCell>
                                <DashboardPdActivityCells goal={g} />
                                <TableCell>{formatDateBrShort(g.startDate)}</TableCell>
                                <TableCell>{formatDateBrShort(g.plannedEndDate)}</TableCell>
                                <TableCell>{formatDateBrShort(g.actualEndDate ?? undefined)}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : null}
            </Paper>

            <Paper sx={SURFACE_SX}>
              <Box sx={{ mb: 2 }}>
                <SectionKicker>Perfil</SectionKicker>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                  Perfil comportamental
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                  Visualização dos seus traços DISC e dimensões comportamentais (somente leitura).
                </Typography>
              </Box>
              <BehavioralChartsPreview user={user} showHeader={false} />
            </Paper>
          </>
        )}

        {showDevelopmentGoalsBlock ? (
          <Paper sx={SURFACE_SX}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, mb: 2 }}
            >
              <Box>
                <SectionKicker>Desenvolvimento</SectionKicker>
                <Typography variant="h6" component="h2" id="dash-team-pd-heading" sx={{ fontWeight: 800 }}>
                  Competências em andamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                  Metas não concluídas por colaborador. Prazo previsto em{' '}
                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>amarelo</Box> na última semana
                  e em <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>vermelho</Box> se
                  vencido.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setTeamPdGoalsOpen((v) => !v)}
                  aria-expanded={teamPdGoalsOpen}
                  aria-controls="dash-team-pd-panel"
                >
                  {teamPdGoalsOpen ? 'Recolher' : 'Expandir'}
                </Button>
                {user.role === 'admin' || user.role === 'hr' ? (
                  <Button component={Link} to="/desenvolvimento-pessoal" variant="outlined" size="small">
                    Gerenciar metas
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            {teamPdGoalsOpen ? (
              <Box id="dash-team-pd-panel" role="region" aria-labelledby="dash-team-pd-heading">
                {pdGoals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma competência em desenvolvimento no momento para o seu escopo.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Colaborador</TableCell>
                          <TableCell>Competência</TableCell>
                          <TableCell>Livro</TableCell>
                          <TableCell>Filme</TableCell>
                          <TableCell>Atividades</TableCell>
                          <TableCell>Início</TableCell>
                          <TableCell>Previsão de término</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pdGoals.map((g) => {
                          const rowSx = pdDeadlineRowSx(g.plannedEndDate, g.developed);
                          const highlight = plannedEndHighlight(g.plannedEndDate, g.developed);
                          return (
                            <TableRow key={g.id} sx={rowSx}>
                              <TableCell>{g.user?.name ?? '—'}</TableCell>
                              <TableCell>{g.competencyTitle}</TableCell>
                              <DashboardPdActivityCells goal={g} />
                              <TableCell>{formatDateBrShort(g.startDate)}</TableCell>
                              <TableCell>
                                <StatusChip
                                  label={formatDateBrShort(g.plannedEndDate)}
                                  tone={plannedEndTone(highlight)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : null}
          </Paper>
        ) : null}

        {(user.role === 'admin' || user.role === 'hr' || user.role === 'manager') && stats.activeCycle ? (
          <Paper sx={SURFACE_SX} aria-labelledby="dash-cycle-team-heading">
            <Box sx={{ mb: 2 }}>
              <SectionKicker>Ciclo ativo</SectionKicker>
              <Typography variant="h6" component="h2" id="dash-cycle-team-heading" sx={{ fontWeight: 800 }}>
                Feedbacks por colaborador
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                Recebimento do gestor e envio do feedback reverso. Linhas em vermelho: falta algum dos dois na
                última semana antes do prazo (ou após o prazo), até concluir.
              </Typography>
            </Box>

            {cycleTeamStatusError ? (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                Não foi possível carregar a lista. Atualize a página ou tente novamente.
              </Typography>
            ) : cycleTeamStatus && cycleTeamStatus.rows.length > 0 ? (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Colaborador</TableCell>
                      <TableCell>Gestor</TableCell>
                      <TableCell>Recebeu do gestor</TableCell>
                      <TableCell>Enviou ao gestor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cycleTeamStatus.rows.map((row) => {
                      const urgent = shouldHighlightTeamFeedbackRow(
                        cycleTeamStatus.deadlineDate,
                        row.filledAt,
                        row.reverseFilledAt,
                      );
                      return (
                        <TableRow
                          key={`${row.developerId}-${row.managerId}`}
                          sx={
                            urgent
                              ? {
                                  backgroundColor: 'rgba(211, 47, 47, 0.10)',
                                  '& td': { color: 'error.main', fontWeight: 600 },
                                }
                              : undefined
                          }
                        >
                          <TableCell>{row.developerName}</TableCell>
                          <TableCell>{row.managerName}</TableCell>
                          <TableCell>
                            {row.filledAt ? (
                              <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
                                {formatDateTimeBr(row.filledAt)}
                              </Box>
                            ) : (
                              <Box
                                component="span"
                                sx={
                                  urgent
                                    ? { color: 'error.main', fontWeight: 700 }
                                    : { color: 'text.secondary' }
                                }
                              >
                                Pendente
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.reverseFilledAt ? (
                              <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
                                {formatDateTimeBr(row.reverseFilledAt)}
                              </Box>
                            ) : (
                              <Box
                                component="span"
                                sx={
                                  urgent
                                    ? { color: 'error.main', fontWeight: 700 }
                                    : { color: 'text.secondary' }
                                }
                              >
                                Pendente
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum colaborador com gestor atribuído neste ciclo.
              </Typography>
            )}
          </Paper>
        ) : null}

        {stats.type === 'manager' && (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Feedbacks enviados</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{stats.totalFeedbacks}</Typography>
                  <Typography variant="body2" color="text.secondary">Total de avaliações criadas para o time.</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Média do time</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{stats.teamAverage}</Typography>
                  <Typography variant="body2" color="text.secondary">Desempenho médio consolidado nas últimas devolutivas.</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Pessoas acompanhadas</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{developersTracked}</Typography>
                  <Typography variant="body2" color="text.secondary">Colaboradores com avaliações registradas.</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <SectionKicker>Time</SectionKicker>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                      Performance por pessoa
                    </Typography>
                  </Box>

                  {teamData.length > 0 ? (
                    <Box sx={{ width: '100%' }}>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={teamData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                          <XAxis dataKey="name" tickLine={false} axisLine={false} />
                          <YAxis tickLine={false} axisLine={false} />
                          <Tooltip />
                          <Bar name="Média" dataKey="average" fill="#2563eb" radius={[10, 10, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Os indicadores do time aparecerão quando houver feedbacks criados.
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <SectionKicker>Ações</SectionKicker>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                      Próximos passos
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Typography sx={{ fontWeight: 800 }}>
                        {stats.activeCycle?.name || 'Sem ciclo ativo'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {stats.activeCycle
                          ? `Prazo final em ${formatCycleDateBr(stats.activeCycle.deadlineDate)}`
                          : 'Ative um ciclo para abrir o período de avaliações.'}
                      </Typography>
                    </Paper>

                    <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                      {cycleSubmissionsOpen ? (
                        <Button component={Link} to="/feedbacks/create" variant="contained" color="primary" startIcon={<AddIcon />}>
                          Criar feedback
                        </Button>
                      ) : (
                        <Paper
                          sx={{
                            p: 1.5,
                            backgroundColor: 'rgba(237, 108, 2, 0.10)',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'warning.main' }}>
                            Envios encerrados após o prazo limite do ciclo. Peça à administração para ampliar o
                            prazo de envio, se necessário.
                          </Typography>
                        </Paper>
                      )}
                      <Button component={Link} to="/feedbacks" variant="outlined">
                        Revisar feedbacks
                      </Button>
                      <Button component={Link} to="/reports" variant="outlined">
                        Abrir relatórios
                      </Button>
                      {canAccessTimeclockReports ? (
                        <Button component={Link} to="/reports?section=timeclock" variant="outlined">
                          Abrir relatórios de ponto
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={SURFACE_SX}>
              <Box sx={{ mb: 2 }}>
                <SectionKicker>Perfil</SectionKicker>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                  DISC e perfil comportamental
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                  Visualização dos seus traços DISC e dimensões comportamentais registados no sistema.
                </Typography>
              </Box>
              <BehavioralChartsPreview user={user} showHeader={false} />
            </Paper>
          </>
        )}

        {stats.type === 'admin' && (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Total de feedbacks</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{stats.totalFeedbacks}</Typography>
                  <Typography variant="body2" color="text.secondary">Volume consolidado de avaliações na plataforma.</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Ciclo ativo</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{stats.activeCycle?.name || '--'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.activeCycle ? formatCycleDateBr(stats.activeCycle.deadlineDate) : 'Nenhum período ativo.'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Gestão central</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem' }}>{user.role === 'hr' ? 'RH' : 'Admin'}</Typography>
                  <Typography variant="body2" color="text.secondary">Controle de usuários, ciclos e acompanhamento geral.</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <SectionKicker>Resumo</SectionKicker>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                      Visão geral da operação
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Typography sx={{ fontWeight: 800 }}>
                        {stats.activeCycle?.name || 'Nenhum ciclo ativo'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {stats.activeCycle
                          ? `Prazo atual em ${formatCycleDateBr(stats.activeCycle.deadlineDate)}`
                          : 'Ative um novo ciclo para iniciar a próxima janela de avaliações.'}
                      </Typography>
                    </Paper>

                    <Typography variant="body2" color="text.secondary">
                      Use os atalhos abaixo para manter a operação organizada e acompanhar a evolução da plataforma.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ ...SURFACE_SX, height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <SectionKicker>Atalhos</SectionKicker>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                      Gestão rápida
                    </Typography>
                  </Box>

                  <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Button component={Link} to="/cycles" variant="contained" color="primary" startIcon={<EventRepeatOutlinedIcon />}>
                      Gerenciar ciclos
                    </Button>
                    <Button component={Link} to="/users" variant="outlined">
                      Gerenciar usuários
                    </Button>
                    <Button component={Link} to="/reports" variant="outlined">
                      Abrir relatórios
                    </Button>
                    {canAccessTimeclockReports ? (
                      <Button component={Link} to="/reports?section=timeclock" variant="outlined">
                        Abrir relatórios de ponto
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>

      <ConfirmModal
        isOpen={punchModalKind !== null}
        title={punchModalTitle}
        message={punchConfirmMessage}
        confirmLabel={punchInProgress ? 'Registrando…' : 'Confirmar'}
        cancelLabel="Cancelar"
        confirmDisabled={punchInProgress}
        tone="neutral"
        onConfirm={() => void executeDashboardPunch()}
        onCancel={() => {
          if (!punchInProgress) setPunchModalKind(null);
        }}
      />

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert((prev) => ({ ...prev, isOpen: false }))}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default Dashboard;
